import {createStore} from 'zustand/vanilla';

import {isNil} from 'ramda';

import type {
  BulkOperationActionContext,
  BulkOperationActionState,
  BulkOperationSelection,
  BulkOperationSelectionSource,
  BulkOperationTarget,
} from '../types/BulkOperationActionCallback';
import {getAvailableBulkOperationTargets} from './getAvailableBulkOperationTargets';

export function getBulkOperationTarget(
  state: BulkOperationActionState
): BulkOperationTarget | null {
  if (state.isClosed || state.requiresConfirmation) {
    return null;
  }
  return getAvailableBulkOperationTargets(state.selection)[state.scope];
}

function selectionChanged(
  previous: BulkOperationSelection,
  next: BulkOperationSelection,
  scope: BulkOperationTarget['type']
) {
  if (previous.allFilteredSelected !== next.allFilteredSelected) {
    return true;
  }
  return scope === 'filtered'
    ? previous.query.dataQueryId !== next.query.dataQueryId
    : JSON.stringify(previous.recordIds.toSorted()) !== JSON.stringify(next.recordIds.toSorted());
}

export function createBulkOperationActionContext(args: {
  source: BulkOperationSelectionSource;
  submit: (target: BulkOperationTarget, params: Record<string, unknown> | null) => Promise<void>;
}) {
  const selection = args.source.getSnapshot();
  const store = createStore<BulkOperationActionState>(() => ({
    selection,
    scope:
      selection.allFilteredSelected || selection.recordIds.length === 0 ? 'filtered' : 'selected',
    isSubmitting: false,
    isClosed: false,
    requiresConfirmation: false,
  }));
  const synchronize = () => {
    if (store.getState().isClosed) {
      return;
    }
    const next = args.source.getSnapshot();
    const current = store.getState();
    store.setState({
      selection: next,
      requiresConfirmation:
        current.requiresConfirmation || selectionChanged(current.selection, next, current.scope),
    });
  };
  const unsubscribe = args.source.subscribe(synchronize);
  const close = () => {
    if (store.getState().isClosed) {
      return;
    }
    unsubscribe();
    store.setState({isClosed: true});
  };
  const context: BulkOperationActionContext = {
    getSnapshot: store.getState,
    subscribe: store.subscribe,
    setScope: (scope) => store.setState({scope}),
    confirmTarget: () => {
      synchronize();
      store.setState({requiresConfirmation: false});
    },
    close,
    submit: async (params) => {
      synchronize();
      const state = store.getState();
      const target = getBulkOperationTarget(state);
      if (state.isSubmitting || isNil(target)) {
        return false;
      }
      store.setState({isSubmitting: true});
      try {
        await args.submit(target, params);
        if (store.getState().isClosed) {
          return false;
        }
        close();
        return true;
      } finally {
        store.setState({isSubmitting: false});
      }
    },
  };
  return {
    ...context,
    dispatch: async (callback: () => void | Promise<void>) => {
      if (store.getState().isClosed) {
        return;
      }
      try {
        await callback();
      } finally {
        close();
      }
    },
  };
}
